import { useState } from 'react'
import AgendaRow from './AgendaRow'
import { BENCHMARK_STATE, effectBenchmarkState } from '../../helpers/intelligence/effectBenchmarkGate'
import ThemeInterviewsSection from './ThemeInterviewsSection'
import ThemePublicationsSection from './ThemePublicationsSection'
import { splitPublishedForTheme } from '../../helpers/intelligence/filterThemeRows'
import { splitAgendaRowsForTheme, splitOwnRowsByFamily } from '../../helpers/intelligence/filterThemeRows'

/**
 * Wat er nog niet berekend of nog niet herberekend is, als eigen zinnen: een
 * lege lijst zonder reden leest als een storing.
 *
 * @param {{version: (string|null), herberekeningNodig: (boolean|null)}} agenda
 * @param {string} waarvoor
 * @returns {string}
 */
const geenCellen = (agenda, waarvoor) => {
    if (agenda.herberekeningNodig === true) {
        return `De cellen zijn nog niet herberekend onder ISS ${agenda.version} — er is net een nieuwe methodiekversie uitgerold. De berekening draait elke nacht om 05:00.`
    }
    return `Nog geen berekende cellen ${waarvoor}${agenda.version ? ` (ISS ${agenda.version})` : ''}. De berekening draait elke nacht om 05:00.`
}

const TABS = [
    { id: 'mechanisms', label: 'Mechanismen' },
    { id: 'activities', label: 'Activiteiten' },
    { id: 'interviews', label: 'Interviews' },
    { id: 'publications', label: 'Publicaties' },
]

/**
 * Inzichten van één thema, in vier tabbladen (besluit user 2026-09-14):
 *
 *   Mechanismen  — segmentcellen van déze doelgroep op de mechanisme-dimensies
 *   Activiteiten — segmentcellen van déze doelgroep op de activiteitsdimensie
 *   Interviews   — wat begeleiders en deelnemers over dit effect bij deze
 *                  doelgroep zeggen
 *   Publicaties  — wat er onder dit thema buiten staat, met nalopen
 *
 * Alleen eigen materiaal: doelgroep- en contextcellen, effectbrede cellen en
 * de TOC-keuzes staan hier niet meer. De cellen hebben de ISS-poort (P2), de
 * interviews hun eigen poort; welke geldt staat per tabblad in de uitleg.
 * AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
 *
 * De benchmarkdrempel van het effect wordt gemarkeerd, niet gefilterd. Drie
 * toestanden per bron; bronnen falen los van elkaar.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object} props
 * @param {object} props.theme - Het thema
 * @param {{rows: Array<object>, p2Targets: (object|null), loading: boolean, loaded: boolean, isEmpty: boolean, version: (string|null), herberekeningNodig: (boolean|null), error: (Error|null)}} props.agenda - Uit useAgenda
 * @param {{keys: Set<string>, loaded: boolean, error: (Error|null)}} props.gepubliceerd - Uit usePublishedCellKeys
 * @param {{data: (object|null), loading: boolean, loaded: boolean, error: (Error|null)}} props.interviews - Uit useThemeInterviews
 * @param {{insights: Array<object>, loaded: boolean, error: (Error|null)}} props.published - Uit usePublishedInsights
 * @param {(questionId: string) => void} props.onOpenPublication - Naar de detailpagina van een publicatie
 * @param {{loaded: boolean, row: (object|null)}} [props.benchmark] - De benchmarkrij van het effect (useEffectBenchmark); zonder dit telt de benchmark als onbekend
 * @param {(row: object) => void} props.onOpenCell - Naar het celdetail van een regel
 * @param {((voorstel: object) => void)|null} props.onOpenInsight - Opent het kwalitatieve inzicht vanaf de interviews; null zolang de inzichten van het thema niet geladen zijn
 * @param {(cel: object) => void} props.onClassificationClick - Opent de uitleg bij een classificatie
 */
const ThemeInsightsSection = ({
    theme, agenda, gepubliceerd, interviews, published, onOpenPublication, benchmark = null, onOpenCell, onOpenInsight = null, onClassificationClick,
}) => {
    const [tab, setTab] = useState('mechanisms')
    // De publicatiepoort op het EFFECT (ADR-040). Onbekend telt als dicht.
    // AI-CONTEXT: See ../../helpers/intelligence/effectBenchmarkGate.js
    const benchmarkState = effectBenchmarkState(benchmark)
    const { own } = splitAgendaRowsForTheme(theme, agenda.rows)
    const perTab = splitOwnRowsByFamily(own)

    const alBuiten = (sleutel) => (gepubliceerd.loaded ? gepubliceerd.keys.has(sleutel) : null)

    const aantallen = {
        mechanisms: agenda.loaded ? perTab.mechanisms.length : null,
        activities: agenda.loaded ? perTab.activities.length : null,
        interviews: interviews.loaded && interviews.data ? interviews.data.own.length : null,
        publications: published.loaded ? splitPublishedForTheme(theme, published.insights).own.length : null,
    }

    const cellen = (rijen, waarvoor) => {
        if (agenda.error) {
            return (
                <p className="intelligence-state intelligence-state--error">
                    De meetgegevens konden niet worden geladen: {agenda.error.message}
                </p>
            )
        }
        if (agenda.loading) return <p className="intelligence-state">Meetgegevens laden…</p>
        return (
            <>
                {benchmarkState === BENCHMARK_STATE.tooWeak && (
                    <p className="intelligence-state">
                        De databasis van dit effect haalt de drempel nog niet: over een patroon
                        binnen het effect valt dan nog niets te beweren, dus publiceren kan nog
                        niet. De cellen staan er wel, zodat te zien is hoe ver ze zijn.
                    </p>
                )}
                {rijen.length === 0
                    ? <p className="intelligence-state">{geenCellen(agenda, `over ${waarvoor} voor deze doelgroep`)}</p>
                    : (
                        <div className="intelligence-agenda-list">
                            {rijen.map((row) => (
                                <AgendaRow
                                    key={row.cellId}
                                    row={row}
                                    alBuiten={alBuiten(row.cellId)}
                                    onOpenCell={onOpenCell}
                                    onClassificationClick={onClassificationClick}
                                    showSegment={false}
                                    benchmarkState={benchmarkState}
                                    p2Targets={agenda.p2Targets}
                                />
                            ))}
                        </div>
                    )}
            </>
        )
    }

    return (
        <section className="intelligence-panel">
            <h3 className="intelligence-panel__title">Inzichten</h3>

            <div className="intelligence-subtabs" role="tablist">
                {TABS.map((entry) => (
                    <button
                        key={entry.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === entry.id}
                        className={`intelligence-subtab${tab === entry.id ? ' intelligence-subtab--active' : ''}`}
                        onClick={() => setTab(entry.id)}
                    >
                        {entry.label}
                        {/* Geen getal zolang de bron niet geladen is: '0' zou een
                            bewering zijn. */}
                        {aantallen[entry.id] !== null && (
                            <span className="intelligence-subtab__aantal">{aantallen[entry.id]}</span>
                        )}
                    </button>
                ))}
            </div>

            {tab === 'mechanisms' && cellen(perTab.mechanisms, 'mechanismen')}
            {tab === 'activities' && cellen(perTab.activities, 'activiteiten')}
            {tab === 'interviews' && (
                <ThemeInterviewsSection theme={theme} interviews={interviews} onOpenInsight={onOpenInsight} embedded />
            )}
            {tab === 'publications' && (
                <ThemePublicationsSection theme={theme} published={published} onOpen={onOpenPublication} embedded />
            )}
        </section>
    )
}

export default ThemeInsightsSection
